import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Users, GraduationCap, TrendingUp, AlertCircle, AlertTriangle, Wallet, BookOpen, Video, CalendarClock, ClipboardList } from "lucide-react";

const isLiveWindow = (l) => {
  if (!l.data || !l.ora_inizio || !l.ora_fine) return false;
  const start = new Date(`${l.data}T${l.ora_inizio}:00`);
  let end = new Date(`${l.data}T${l.ora_fine}:00`);
  if (end <= start) end = new Date(end.getTime() + 24*3600*1000);
  const now = new Date();
  return now >= new Date(start.getTime() - 15*60000) && now <= new Date(end.getTime() + 45*60000);
};

function KpiCard({ icon: Icon, label, value, sub, tid, to, color = "#F7941E" }) {
  return (
    <Link to={to} className="block h-full" data-testid={tid}>
      <Card className="p-5 bg-white border-[#E2E8F0] hover:shadow-md hover:border-[#F7941E] transition-all cursor-pointer h-full">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold">{label}</div>
            <div className="text-3xl font-bold mt-2" style={{color}}>{value}</div>
            {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{background: color+"15"}}>
            <Icon className="w-5 h-5" style={{color}}/>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  const [kpi, setKpi] = useState(null);
  const [live, setLive] = useState([]);
  const [classi, setClassi] = useState([]);
  const [upLezioni, setUpLezioni] = useState([]);
  const [upTest, setUpTest] = useState([]);
  const [residuo, setResiduo] = useState(null);
  const [sogliaResiduo, setSogliaResiduo] = useState(() => Number(localStorage.getItem("iste_soglia_residuo")) || 1000);

  useEffect(() => {
    if (user?.role !== "admin" && user?.role !== "collaboratore") return;
    api.get("/dashboard/kpi").then(r => setKpi(r.data)).catch(()=>{});
    if (user?.role === "admin") api.get("/dashboard/residuo-classi").then(r => setResiduo(r.data)).catch(()=>{});
    const loadLive = () => Promise.all([api.get("/lezioni"), api.get("/classi")])
      .then(([l, c]) => { setClassi(c.data); setLive(l.data.filter(isLiveWindow)); })
      .catch(() => {});
    loadLive();
    const t = setInterval(loadLive, 60000);
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    if (!user || user.role === "admin" || user.role === "collaboratore") return;
    const oggi = new Date().toISOString().slice(0, 10);
    api.get("/lezioni").then(r => setUpLezioni(
      r.data.filter(l => l.data >= oggi).sort((a,b)=> (a.data+a.ora_inizio).localeCompare(b.data+b.ora_inizio)).slice(0, 6)
    )).catch(()=>{});
    api.get("/test").then(r => setUpTest(
      (user.role === "studente"
        ? r.data.filter(t => !t.mia_consegna && (!t.scadenza || t.scadenza >= oggi))
        : r.data
      ).slice(0, 6)
    )).catch(()=>{});
  }, [user]);

  if (user?.role !== "admin" && user?.role !== "collaboratore") {
    const roleLabel = user?.role === "docente" ? "Docente Formatore" : "Allievo / Studente";
    return (
      <div className="p-4 sm:p-8 max-w-5xl">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Benvenuto</div>
        <h1 className="text-3xl font-bold text-stone-900 mb-1">Ciao {user?.name} 👋</h1>
        <p className="text-stone-600 mb-6">{user?.role === "docente" ? "Ecco le tue prossime lezioni e i test assegnati." : "Ecco le tue prossime lezioni e i test da consegnare."}</p>

        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 bg-white border-[#E2E8F0]" data-testid="dash-prossime-lezioni">
            <div className="flex items-center gap-2 mb-4"><CalendarClock className="w-5 h-5 text-[#1F4E79]"/><h3 className="text-lg font-bold text-stone-900">Prossime lezioni</h3></div>
            {upLezioni.length === 0 ? <div className="text-sm text-stone-500">Nessuna lezione programmata.</div> : (
              <div className="space-y-2">
                {upLezioni.map(l => (
                  <div key={l.id} className="border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid={`dash-lezione-${l.id}`}>
                    <div className="font-medium text-stone-900 text-sm">{l.titolo || l.materia || "Lezione"}</div>
                    <div className="text-xs text-stone-500">{l.data} · {l.ora_inizio}-{l.ora_fine}{l.materia ? ` · ${l.materia}` : ""}</div>
                  </div>
                ))}
                <Link to="/app/calendario" className="text-xs text-[#F7941E] hover:underline inline-block mt-1">Apri calendario →</Link>
              </div>
            )}
          </Card>

          <Card className="p-6 bg-white border-[#E2E8F0]" data-testid="dash-prossimi-test">
            <div className="flex items-center gap-2 mb-4"><ClipboardList className="w-5 h-5 text-[#F7941E]"/><h3 className="text-lg font-bold text-stone-900">{user?.role === "studente" ? "Test da consegnare" : "Test assegnati"}</h3></div>
            {upTest.length === 0 ? <div className="text-sm text-stone-500">{user?.role === "studente" ? "Nessun test in sospeso. Ottimo!" : "Nessun test attivo."}</div> : (
              <div className="space-y-2">
                {upTest.map(t => (
                  <div key={t.id} className="border border-[#E2E8F0] rounded-lg px-3 py-2" data-testid={`dash-test-${t.id}`}>
                    <div className="font-medium text-stone-900 text-sm">{t.titolo}</div>
                    <div className="text-xs text-stone-500">{t.classe_nome} · scadenza {t.scadenza}</div>
                  </div>
                ))}
                <Link to="/app/test" className="text-xs text-[#F7941E] hover:underline inline-block mt-1">Vai all'area test →</Link>
              </div>
            )}
          </Card>
        </div>

        <Card className="mt-6 p-4 bg-[#FFF5EB] border-[#F5D9B8]">
          <div className="text-[#C96F0E] font-semibold text-sm">Ruolo attivo</div>
          <div className="text-sm text-stone-700">{roleLabel}</div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Quadro Direzionale</div>
        <h1 className="text-3xl font-bold text-stone-900">Dashboard Amministrativa</h1>
        <p className="text-stone-600 mt-1">Panoramica in tempo reale · Compliance Regione Calabria · Clicca un riquadro per aprire la sezione</p>
      </div>
      {kpi ? (
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
          <KpiCard icon={Users} label="Allievi Iscritti" value={kpi.allievi_attivi} sub={`${kpi.docenti} docenti attivi`} tid="kpi-allievi-attivi" to="/app/anagrafiche"/>
          {user?.role === "admin" && <KpiCard icon={Wallet} label="Incassato" value={`€ ${kpi.incassato.toLocaleString('it-IT', {minimumFractionDigits:2})}`} sub={`€ ${kpi.da_incassare.toLocaleString('it-IT')} da incassare`} tid="kpi-incassato" to="/app/registro-pagamenti" color="#1E7E55"/>}
          {user?.role === "admin" && <KpiCard icon={AlertCircle} label="Rate Insolute" value={kpi.insoluti_pendenti} sub="Scadute non pagate" tid="kpi-insoluti" to="/app/solleciti" color="#C53030"/>}
          {user?.role === "admin" && <KpiCard icon={BookOpen} label="Iscrizioni Nuove" value={kpi.iscrizioni_nuove} sub="Da valutare" tid="kpi-iscrizioni-nuove" to="/app/iscrizioni" color="#1F4E79"/>}
        </div>
      ) : <div className="text-stone-500">Caricamento...</div>}

      <Card className="mt-6 p-6 bg-white border-[#E2E8F0]" data-testid="live-now-section">
        <div className="flex items-center gap-2 mb-3">
          <Video className="w-5 h-5 text-emerald-600"/>
          <h3 className="text-xl font-bold text-stone-900">Lezioni in corso adesso</h3>
          {live.length > 0 && <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="live-now-count">{live.length} attiv{live.length === 1 ? "a" : "e"}</span>}
        </div>
        {live.length === 0 ? (
          <div className="text-sm text-stone-500">Nessuna lezione in corso al momento.</div>
        ) : (
          <div className="space-y-2">
            {live.map(l => {
              const cl = classi.find(x => x.id === l.classe_id);
              const docNome = cl?.docenti_dettagli?.find(x => x.id === l.docente_id)?.nome;
              return (
                <div key={l.id} className="flex flex-wrap items-center gap-3 border border-[#E2E8F0] rounded-lg px-4 py-3" data-testid={`live-now-${l.id}`}>
                  <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold" data-testid={`live-badge-${l.id}`}>
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
                    LIVE{typeof l.live_studenti === "number" ? ` · ${l.live_studenti} collegat${l.live_studenti === 1 ? "o" : "i"}` : ""}
                  </span>
                  <span className="font-semibold text-stone-900">{l.titolo}</span>
                  <span className="text-sm text-stone-500">{l.ora_inizio} - {l.ora_fine}{l.materia ? ` · ${l.materia}` : ""}</span>
                  <span className="text-sm text-[#F7941E]">{cl?.nome}</span>
                  {docNome && <span className="text-sm text-stone-600">· {docNome}</span>}
                  <Link to="/app/calendario" className="ml-auto text-xs text-[#F7941E] hover:underline">Apri calendario</Link>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <div className="mt-10">
        {user?.role === "admin" && (
        <Card className="p-6 bg-white border-[#E2E8F0]" data-testid="residuo-classi-section">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Wallet className="w-5 h-5 text-[#C53030]"/>
            <h3 className="text-xl font-bold text-stone-900">Residuo da incassare per classe</h3>
            {residuo && <span className="text-xs font-semibold text-[#C53030] bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid="residuo-totale">€ {residuo.totale_residuo.toLocaleString('it-IT', {minimumFractionDigits:2})} totali</span>}
            <div className="ml-auto flex items-center gap-2 text-xs text-stone-500">
              <span>Soglia avviso €</span>
              <input type="number" min="0" step="50" value={sogliaResiduo}
                onChange={e => { const v = Math.max(0, Number(e.target.value) || 0); setSogliaResiduo(v); localStorage.setItem("iste_soglia_residuo", v); }}
                className="w-24 h-8 border border-[#E2E8F0] rounded-md px-2 text-stone-900" data-testid="residuo-soglia-input"/>
            </div>
          </div>
          {residuo && (() => {
            const alte = residuo.classi.filter(c => c.residuo >= sogliaResiduo && c.residuo > 0);
            return alte.length > 0 && (
              <div className="flex items-start gap-2 mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-800" data-testid="residuo-alert-banner">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5"/>
                <div className="text-sm">
                  <span className="font-semibold" data-testid="residuo-alert-count">{alte.length} class{alte.length === 1 ? "e" : "i"}</span> {alte.length === 1 ? "supera" : "superano"} la soglia di € {Number(sogliaResiduo).toLocaleString('it-IT')}: <span className="font-medium">{alte.map(c => c.nome).join(", ")}</span>. Valuta di inviare un sollecito.
                </div>
              </div>
            );
          })()}
          {!residuo ? <div className="text-sm text-stone-500">Caricamento...</div> : residuo.classi.length === 0 ? (
            <div className="text-sm text-stone-500">Nessuna classe.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {residuo.classi.map(c => {
                const sopra = c.residuo >= sogliaResiduo && c.residuo > 0;
                return (
                <Link key={c.classe_id} to="/app/registro-pagamenti" className={`relative border rounded-lg px-4 py-3 hover:shadow-sm transition-all ${sopra ? "border-red-300 bg-red-50/60 hover:border-red-400" : "border-[#E2E8F0] hover:border-[#F7941E]"}`} data-testid={`residuo-classe-${c.classe_id}`}>
                  {sopra && <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-red-700 bg-white border border-red-300 rounded-full px-2 py-0.5" data-testid={`residuo-alto-${c.classe_id}`}><AlertTriangle className="w-3 h-3"/>Sopra soglia</span>}
                  <div className="font-semibold text-stone-900">{c.nome}</div>
                  <div className="text-xs text-stone-500 mt-1">Concordato € {c.cifra.toLocaleString('it-IT')} · Versato € {c.versato.toLocaleString('it-IT')}</div>
                  <div className={`text-lg font-bold mt-1 ${c.residuo > 0 ? "text-[#C53030]" : "text-emerald-700"}`}>Residuo € {c.residuo.toLocaleString('it-IT', {minimumFractionDigits:2})}</div>
                </Link>
              );})}
            </div>
          )}
        </Card>
        )}
      </div>
    </div>
  );
}
